import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Platform-admin API client (SCRUM-391) over the SCRUM-390 backend. The
 * /api/admin surface is NOT tenant-scoped — no X-Tenant-Id header (the
 * middleware ignores it there anyway; it operates above tenant boundaries).
 *
 * AUTHORIZATION LIMITATION: server-side the surface is open until
 * SCRUM-102/103 — the Platform Admin role gate is UI-only (see ADMIN.md).
 */
const ADMIN_TENANTS_URL = '/api/admin/tenants';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

const tenantCardSchema = z.object({
  id: z.string(),
  // SCRUM-1012: stable, seed-invariant IBM App ID key. Used as an unambiguous DOM hook
  // (data-tenant-appid) so a specific tenant is addressable even when two tenants share a
  // display name (the SCRUM-1008 demo + synthetic "Meridian Community Bank" case).
  appIdTenantId: z.string(),
  bankName: z.string(),
  status: z.enum(['active', 'inactive', 'closed']),
  charter: z.string().nullable(),
  region: z.string().nullable(),
  lastBatchAt: z.string().nullable(),
  users: z.number(),
  insights: z.number(),
  campaigns: z.number(),
});

export type TenantCard = z.infer<typeof tenantCardSchema>;

const adminErrorSchema = z.object({ error: z.string(), message: z.string() });

async function throwAdminError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const parsed = adminErrorSchema.safeParse(await res.json());
    if (parsed.success) message = parsed.data.message;
  } catch {
    // Non-JSON body — keep the fallback.
  }
  throw new Error(message);
}

/** GET /api/admin/tenants — all customer banks as admin cards. */
export async function fetchTenants(): Promise<TenantCard[]> {
  const res = await apiFetch(ADMIN_TENANTS_URL);
  if (!res.ok) {
    throw new Error(`GET ${ADMIN_TENANTS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return tenantCardSchema.array().parse(raw);
}

/** The onboarding wizard's full captured configuration (steps 2–5 + display stats). */
export interface OnboardingConfig {
  intelligence: Record<string, Record<string, string | number>>;
  products: OnboardingProduct[];
  vaultFiles: string[];
  roles: OnboardingRole[];
  users: OnboardingUser[];
}

export interface EligibilityRule {
  field: string;
  operator: string;
  value: string;
  required: boolean;
}

export interface OnboardingProduct {
  name: string;
  category: string;
  description: string;
  rules: EligibilityRule[];
  requiredMatches: number;
}

export interface OnboardingRole {
  name: string;
  description: string;
}

export interface OnboardingUser {
  name: string;
  email: string;
  role: string;
}

export interface CreateTenantInput {
  bankName: string;
  charter: string;
  region: string;
  primaryContactName: string;
  primaryContactEmail: string;
  config: OnboardingConfig;
}

/** POST /api/admin/tenants — creates a Draft tenant from the wizard. */
export async function createTenant(input: CreateTenantInput): Promise<string> {
  const res = await apiFetch(ADMIN_TENANTS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      bankName: input.bankName,
      charter: input.charter || null,
      region: input.region || null,
      primaryContactName: input.primaryContactName,
      primaryContactEmail: input.primaryContactEmail,
      onboardingConfigJson: JSON.stringify(input.config),
    }),
  });
  if (!res.ok) {
    await throwAdminError(res, `Creating ${input.bankName} failed (${String(res.status)}).`);
  }
  const body = (await res.json()) as { id: string };
  return body.id;
}

/** A real team member row (SCRUM-433 detail endpoint). */
const teamUserSchema = z.object({
  displayName: z.string(),
  email: z.string(),
  role: z.string(),
});

/** A real vault document row (SCRUM-433 detail endpoint). */
const vaultDocumentSchema = z.object({
  fileName: z.string(),
  sizeBytes: z.number(),
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * Full Manage Account profile (SCRUM-433). Org fields + the raw onboarding_config
 * JSON (parsed client-side into the wizard's OnboardingConfig sections) + REAL team
 * users and vault documents. Team/Vault are empty for non-provisioned showcase tenants.
 */
const tenantDetailSchema = z.object({
  id: z.string(),
  bankName: z.string(),
  status: z.enum(['active', 'inactive', 'closed']),
  charter: z.string().nullable(),
  region: z.string().nullable(),
  lastBatchAt: z.string().nullable(),
  activatedAt: z.string().nullable(),
  onboardingConfigJson: z.string().nullable(),
  team: teamUserSchema.array(),
  vault: vaultDocumentSchema.array(),
  // SCRUM-575: the tenant's verified email domains (login routing).
  emailDomains: z.string().array(),
  // SCRUM-585: IdP federation config. clientSecretRef is a Secrets Manager
  // NAME (non-sensitive identifier) — the secret VALUE never reaches the SPA.
  // nullish+default: a payload from an older API build parses as unconfigured
  // rather than failing the whole detail view.
  idpDiscoveryUrl: z.string().nullish().default(null),
  idpClientId: z.string().nullish().default(null),
  idpClientSecretRef: z.string().nullish().default(null),
  idpScopes: z.string().nullish().default(null),
  // SCRUM-594: lets the Activate panel mirror the server's contact-domain
  // readiness rule. nullish-tolerant for older API payloads.
  primaryContactEmail: z.string().nullish().default(null),
});

export type TenantDetail = z.infer<typeof tenantDetailSchema>;
export type TeamUser = z.infer<typeof teamUserSchema>;
export type VaultDocument = z.infer<typeof vaultDocumentSchema>;

/** GET /api/admin/tenants/{id} — full Manage Account profile. */
export async function fetchTenantDetail(id: string): Promise<TenantDetail> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}`);
  if (!res.ok) {
    await throwAdminError(res, `Loading the account failed (${String(res.status)}).`);
  }
  const raw: unknown = await res.json();
  return tenantDetailSchema.parse(raw);
}

/** The config-backed sections a PATCH may carry (all optional — per-key merge). */
export interface TenantConfigPatch {
  bankName?: string;
  charter?: string;
  region?: string;
  intelligenceJson?: string;
  productsJson?: string;
  // SCRUM-575: omit to leave domains unchanged; a list (incl. empty) replaces them.
  emailDomains?: string[];
}

/** PATCH /api/admin/tenants/{id}/config — persist config-backed sections. */
export async function patchTenantConfig(id: string, patch: TenantConfigPatch): Promise<void> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/config`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      bankName: patch.bankName ?? null,
      status: null,
      charter: patch.charter ?? null,
      region: patch.region ?? null,
      intelligenceJson: patch.intelligenceJson ?? null,
      productsJson: patch.productsJson ?? null,
      // undefined -> null (facet not edited); a list is sent as-is (replace).
      emailDomains: patch.emailDomains ?? null,
    }),
  });
  if (!res.ok) {
    await throwAdminError(res, `Saving changes failed (${String(res.status)}).`);
  }
}

/** POST /api/admin/tenants/{id}/close — Suspend with a recorded reason. */
export async function closeTenant(id: string, reason: string, notes: string): Promise<void> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, notes: notes || null }),
  });
  if (!res.ok) {
    await throwAdminError(res, `Closing the account failed (${String(res.status)}).`);
  }
}

// ── Provisioning completeness (SCRUM-585) ───────────────────────────────────

/** The tenant's bank-IdP OIDC config. The secret ref is a Secrets Manager NAME. */
export interface TenantIdpConfig {
  discoveryUrl: string;
  clientId: string;
  clientSecretRef: string;
  // `| undefined` keeps object literals assignable under exactOptionalPropertyTypes.
  scopes?: string | undefined;
}

/** PATCH /api/admin/tenants/{id}/idp — configure the tenant's IdP federation. */
export async function patchTenantIdp(id: string, idp: TenantIdpConfig): Promise<void> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/idp`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      discoveryUrl: idp.discoveryUrl,
      clientId: idp.clientId,
      clientSecretRef: idp.clientSecretRef,
      scopes: idp.scopes ?? null,
    }),
  });
  if (!res.ok) {
    await throwAdminError(res, `Saving the SSO configuration failed (${String(res.status)}).`);
  }
}

/**
 * POST /api/admin/tenants/{id}/activate — fail-closed activation: the server
 * rejects (400) unless IdP config AND at least one email domain are present,
 * then provisions the schema, activates, and seeds the primary-contact user.
 */
export async function activateTenant(id: string): Promise<void> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/activate`, { method: 'POST' });
  if (!res.ok) {
    await throwAdminError(res, `Activating the account failed (${String(res.status)}).`);
  }
}

/**
 * Roles assignable through a mapping rule: the four stored bank-side
 * SystemRole names. PlatformAdmin is deliberately absent — it is never
 * mappable from tenant data (SCRUM-287 invariant, enforced server-side too).
 * Render via toDisplayRole (roles.ts single-source stored→display mapping).
 */
export const MAPPABLE_STORED_ROLES = [
  'Executive',
  'BankOfficer',
  'BranchManager',
  'BranchEmployee',
] as const;

/** One IdP-group → BankIQ-role mapping row (stored SystemRole names). */
const roleMappingSchema = z.object({
  idpGroupClaim: z.string(),
  grantedRole: z.string(),
});

export type RoleMapping = z.infer<typeof roleMappingSchema>;

/** GET /api/admin/tenants/{id}/role-mappings — the tenant's mapping rules. */
export async function fetchRoleMappings(id: string): Promise<RoleMapping[]> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/role-mappings`);
  if (!res.ok) {
    await throwAdminError(res, `Loading role mappings failed (${String(res.status)}).`);
  }
  const raw: unknown = await res.json();
  return roleMappingSchema.array().parse(raw);
}

/** PUT /api/admin/tenants/{id}/role-mappings — replace the rules wholesale. */
export async function putRoleMappings(id: string, mappings: RoleMapping[]): Promise<void> {
  const res = await apiFetch(`${ADMIN_TENANTS_URL}/${id}/role-mappings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mappings }),
  });
  if (!res.ok) {
    await throwAdminError(res, `Saving role mappings failed (${String(res.status)}).`);
  }
}

// ── User Activity (SCRUM-575) ────────────────────────────────────────────────

const ADMIN_USERS_URL = '/api/admin/users';

const userActivitySchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  displayName: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
});

export type UserActivity = z.infer<typeof userActivitySchema>;

/** GET /api/admin/users[?tenantId=] — cross-tenant user-activity list. */
export async function fetchUserActivity(tenantId?: string): Promise<UserActivity[]> {
  const url = tenantId
    ? `${ADMIN_USERS_URL}?tenantId=${encodeURIComponent(tenantId)}`
    : ADMIN_USERS_URL;
  const res = await apiFetch(url);
  if (!res.ok) {
    await throwAdminError(res, `Loading user activity failed (${String(res.status)}).`);
  }
  const raw: unknown = await res.json();
  return userActivitySchema.array().parse(raw);
}
